import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { DEAL_STAGES, getStageName, parseItems, parseItemLine } from '../../utils/dealConstants'
import DealDetailModal from './DealDetailModal'

function AllDeals() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [allDealsCache, setAllDealsCache] = useState([])
  const [expandedDeals, setExpandedDeals] = useState(new Set())

  // Check for dealId, stage, or email in URL to auto-open/filter
  // Using searchParams as dependency so it re-runs when URL params change
  useEffect(() => {
    const dealIdParam = searchParams.get('dealId')
    const stageParam = searchParams.get('stage')
    const emailParam = searchParams.get('email')

    if (dealIdParam) {
      // Load this specific deal and open its modal
      loadAndOpenDeal(dealIdParam)
      // Clean up URL by removing the dealId param
      setSearchParams({}, { replace: true })
    } else if (emailParam) {
      // Pre-fill email and auto-search
      setSearchFilters(prev => ({ ...prev, email: emailParam }))
      setAdvancedExpanded(true) // Show the email field
      // Clean up URL
      setSearchParams({}, { replace: true })
      // Trigger search after a short delay to let state update
      setTimeout(() => {
        searchDealsWithEmail(emailParam)
      }, 100)
    } else if (stageParam) {
      // Pre-filter by stage and auto-search
      setSearchFilters(prev => ({ ...prev, dealStage: stageParam }))
      // Clean up URL
      setSearchParams({}, { replace: true })
      // Trigger search after a short delay to let state update
      setTimeout(() => {
        searchDealsWithStage(stageParam)
      }, 100)
    }
  }, [searchParams])

  // Search with a specific stage (for URL-based filtering)
  async function searchDealsWithStage(stage) {
    setLoading(true)
    setHasSearched(true)
    try {
      let dealsToFilter
      if (allDealsCache.length === 0) {
        const response = await api.get('/api/deals?all=true')
        dealsToFilter = response.deals || []
        setAllDealsCache(dealsToFilter)
      } else {
        dealsToFilter = allDealsCache
      }

      const filtered = dealsToFilter.filter(deal => deal.dealstage === stage)
      setDeals(filtered)
    } catch (error) {
      console.error('Error searching deals:', error)
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  // Search with a specific email (for URL-based filtering from Tickets)
  async function searchDealsWithEmail(email) {
    setLoading(true)
    setHasSearched(true)
    try {
      // Use the email search endpoint
      const response = await api.get(`/api/deals?email=${encodeURIComponent(email)}`)
      setDeals(response.deals || [])
    } catch (error) {
      console.error('Error searching deals by email:', error)
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  async function loadAndOpenDeal(dealId) {
    try {
      setLoading(true)
      const response = await api.get(`/api/deals/${dealId}`)
      if (response.deal) {
        setSelectedDeal(response.deal)
      }
    } catch (error) {
      console.error('Error loading deal:', error)
    } finally {
      setLoading(false)
    }
  }

  // Advanced search filters
  const [searchFilters, setSearchFilters] = useState({
    email: '',
    dealId: '',
    parsedItems: '',
    dateFrom: '',
    dateTo: '',
    dealStage: '',
    trackingNumber: '',
    collectionDateFrom: '',
    collectionDateTo: '',
    noCollectionDate: false
  })
  const [advancedExpanded, setAdvancedExpanded] = useState(false)

  // Deal detail modal
  const [selectedDeal, setSelectedDeal] = useState(null)

  // Search deals
  async function searchDeals() {
    setLoading(true)
    setHasSearched(true)
    try {
      let dealsToFilter
      const hasDateFilter = searchFilters.dateFrom || searchFilters.dateTo

      // If deal ID is provided, use server-side direct lookup
      if (searchFilters.dealId) {
        const response = await api.get(`/api/deals?dealId=${encodeURIComponent(searchFilters.dealId)}`)
        dealsToFilter = response.deals || []
      } else if (searchFilters.email) {
        // If email is provided, use server-side email search
        const response = await api.get(`/api/deals?email=${encodeURIComponent(searchFilters.email)}`)
        dealsToFilter = response.deals || []
      } else if (hasDateFilter) {
        // Date filter - use server-side date filtering
        const params = new URLSearchParams({ all: 'true' })
        if (searchFilters.dateFrom) params.append('dateFrom', searchFilters.dateFrom)
        if (searchFilters.dateTo) params.append('dateTo', searchFilters.dateTo)
        const response = await api.get(`/api/deals?${params.toString()}`)
        dealsToFilter = response.deals || []
      } else {
        // No specific filter - use cached all deals
        if (allDealsCache.length === 0) {
          const response = await api.get('/api/deals?all=true')
          dealsToFilter = response.deals || []
          setAllDealsCache(dealsToFilter)
        } else {
          dealsToFilter = allDealsCache
        }
      }

      // Apply client-side filters (deal ID is now server-side)
      const filtered = dealsToFilter.filter(deal => {
        // Parsed Items filter
        if (searchFilters.parsedItems) {
          const itemsLower = searchFilters.parsedItems.toLowerCase()
          if (!(deal.parsed_items || '').toLowerCase().includes(itemsLower)) return false
        }

        // Deal Stage filter
        if (searchFilters.dealStage) {
          if (deal.dealstage !== searchFilters.dealStage) return false
        }

        // Tracking Number filter (supports comma-separated list)
        if (searchFilters.trackingNumber) {
          const dealTracking = (deal.tracking_number || '').toLowerCase()
          const searchTerms = searchFilters.trackingNumber.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          if (!searchTerms.some(term => dealTracking.includes(term))) return false
        }

        // No Collection Date filter
        if (searchFilters.noCollectionDate) {
          if (deal.collection_date) return false
        }

        // Collection Date From
        if (searchFilters.collectionDateFrom && !searchFilters.noCollectionDate) {
          if (!deal.collection_date) return false
          const collDate = new Date(deal.collection_date)
          const fromDate = new Date(searchFilters.collectionDateFrom)
          if (collDate < fromDate) return false
        }

        // Collection Date To
        if (searchFilters.collectionDateTo && !searchFilters.noCollectionDate) {
          if (!deal.collection_date) return false
          const collDate = new Date(deal.collection_date)
          const toDate = new Date(searchFilters.collectionDateTo)
          toDate.setHours(23, 59, 59, 999)
          if (collDate > toDate) return false
        }

        return true
      })

      setDeals(filtered)
    } catch (error) {
      console.error('Error searching deals:', error)
      setDeals([])
    } finally {
      setLoading(false)
    }
  }

  // Clear filters
  function clearFilters() {
    setSearchFilters({
      email: '',
      dealId: '',
      parsedItems: '',
      dateFrom: '',
      dateTo: '',
      dealStage: '',
      trackingNumber: '',
      collectionDateFrom: '',
      collectionDateTo: '',
      noCollectionDate: false
    })
    setAdvancedExpanded(false)
    setDeals([])
    setHasSearched(false)
  }

  // Refresh cache
  function refreshCache() {
    setAllDealsCache([])
    setDeals([])
    setHasSearched(false)
  }

  // Update search filter
  function updateFilter(field, value) {
    setSearchFilters(prev => ({ ...prev, [field]: value }))
  }

  // Open deal in modal
  function openDealModal(deal) {
    setSelectedDeal(deal)
  }

  // Close modal
  function closeDealModal() {
    setSelectedDeal(null)
  }

  // Refresh deals after modal changes
  function refreshDeals() {
    setAllDealsCache([])
    if (hasSearched) searchDeals()
  }

  // Calculate stats from current results
  const totalValue = deals.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0)
  const totalItems = deals.reduce((sum, d) => sum + parseItems(d.parsed_items).length, 0)

  // Count by stage
  const stageCounts = deals.reduce((acc, d) => {
    const stage = getStageName(d.dealstage)
    acc[stage] = (acc[stage] || 0) + 1
    return acc
  }, {})

  // Get top stages
  const topStages = Object.entries(stageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)

  return (
    <div>
      {/* Stats */}
      {hasSearched && deals.length > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{deals.length}</div>
            <div className="stat-label">Deals Found</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{totalItems}</div>
            <div className="stat-label">Total Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{formatCurrency(totalValue)}</div>
            <div className="stat-label">Total Value</div>
          </div>
          {topStages.map(([stage, count], i) => (
            <div className="stat-card" key={i}>
              <div className="stat-value">{count}</div>
              <div className="stat-label">{stage}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search Filters */}
      <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        {/* Primary filters */}
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Email</label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by email..."
              value={searchFilters.email}
              onChange={e => updateFilter('email', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchDeals()}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Deal ID</label>
            <input
              type="text"
              className="form-input"
              placeholder="ID..."
              value={searchFilters.dealId}
              onChange={e => updateFilter('dealId', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchDeals()}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Stage</label>
            <select
              className="form-input"
              value={searchFilters.dealStage}
              onChange={e => updateFilter('dealStage', e.target.value)}
            >
              {DEAL_STAGES.map(stage => (
                <option key={stage.id} value={stage.id}>{stage.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Toggle */}
        <button
          type="button"
          className="filter-toggle"
          onClick={() => setAdvancedExpanded(!advancedExpanded)}
        >
          <span className={`filter-toggle-icon ${advancedExpanded ? 'expanded' : ''}`}>▼</span>
          {advancedExpanded ? 'Hide' : 'Show'} Advanced Search
        </button>

        {/* Advanced filters */}
        {advancedExpanded && (
          <div className="advanced-filters">
            <div className="filter-section">
              <div className="filter-group">
                <label className="filter-label">Parsed Items</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search in items..."
                  value={searchFilters.parsedItems}
                  onChange={e => updateFilter('parsedItems', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Tracking Number</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Tracking (comma-separated)..."
                  value={searchFilters.trackingNumber}
                  onChange={e => updateFilter('trackingNumber', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created From</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.dateFrom}
                  onChange={e => updateFilter('dateFrom', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Created To</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.dateTo}
                  onChange={e => updateFilter('dateTo', e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Collection From</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.collectionDateFrom}
                  onChange={e => updateFilter('collectionDateFrom', e.target.value)}
                  disabled={searchFilters.noCollectionDate}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Collection To</label>
                <input
                  type="date"
                  className="form-input"
                  value={searchFilters.collectionDateTo}
                  onChange={e => updateFilter('collectionDateTo', e.target.value)}
                  disabled={searchFilters.noCollectionDate}
                />
              </div>
              <div className="filter-group" style={{ display: 'flex', alignItems: 'center', paddingTop: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={searchFilters.noCollectionDate}
                    onChange={e => updateFilter('noCollectionDate', e.target.checked)}
                  />
                  No collection date
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)' }}>
          <button type="button" className="btn btn-secondary" onClick={clearFilters}>
            Clear
          </button>
          <button type="button" className="btn btn-primary" onClick={searchDeals} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasSearched && deals.length > 0 && (
            <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
              {deals.length} deals found
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="card">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : !hasSearched ? (
          <div className="empty-state">
            <div className="empty-state-title">Search Deals</div>
            <div className="empty-state-description">
              Enter search criteria and click Search to find deals.
            </div>
          </div>
        ) : deals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No Deals Found</div>
            <div className="empty-state-description">
              Try adjusting your search filters.
            </div>
          </div>
        ) : (
          <>
            {/* Results header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-3)',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-bg-secondary)'
            }}>
              <span className="text-muted">{deals.length} deals</span>
              <button className="btn btn-xs btn-secondary" onClick={refreshCache}>Refresh</button>
            </div>

            {/* Results list */}
            <div style={{ padding: 'var(--space-3)' }}>
              {deals.map(deal => {
                const items = parseItems(deal.parsed_items)
                const firstItem = items.length > 0 ? parseItemLine(items[0]) : null
                const moreCount = items.length - 1
                const isExpanded = expandedDeals.has(deal.id)

                const toggleExpand = (e) => {
                  e.stopPropagation()
                  setExpandedDeals(prev => {
                    const next = new Set(prev)
                    if (next.has(deal.id)) {
                      next.delete(deal.id)
                    } else {
                      next.add(deal.id)
                    }
                    return next
                  })
                }

                return (
                  <div
                    key={deal.id}
                    style={{
                      borderBottom: '1px solid var(--color-border)'
                    }}
                  >
                    <div
                      onClick={() => openDealModal(deal)}
                      style={{
                        padding: 'var(--space-3)',
                        cursor: 'pointer',
                        display: 'flex',
                        gap: 'var(--space-3)',
                        alignItems: 'flex-start'
                      }}
                      className="hover-row"
                    >
                      {/* Main content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Item name and +more */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            {firstItem?.name || deal.dealname}
                          </span>
                          {moreCount > 0 && (
                            <button
                              onClick={toggleExpand}
                              style={{
                                fontSize: '0.75rem',
                                color: 'var(--color-primary)',
                                background: 'var(--color-bg-secondary)',
                                padding: '2px 8px',
                                borderRadius: 4,
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              {isExpanded ? '− hide' : `+${moreCount} more`}
                            </button>
                          )}
                        </div>

                        {/* Item details */}
                        {firstItem?.details && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            {firstItem.details}
                          </div>
                        )}

                        {/* Qty, SP, BP */}
                        <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: '0.8rem', marginBottom: 4 }}>
                          {firstItem && (
                            <>
                              <span>×{firstItem.qty || 1}</span>
                              {firstItem.sp != null && <span style={{ color: 'var(--color-success)' }}>SP £{firstItem.sp}</span>}
                              {firstItem.bp != null && <span style={{ color: 'var(--color-info)' }}>BP £{firstItem.bp}</span>}
                            </>
                          )}
                        </div>

                        {/* Email and Deal ID */}
                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}>#{deal.id}</span>
                          <span>{deal.seller_email || deal.contact_email || '-'}</span>
                        </div>
                      </div>

                      {/* Right side - Stage and date */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span className="badge">{getStageName(deal.dealstage)}</span>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                          {formatDate(deal.createdate)}
                        </div>
                      </div>
                    </div>

                    {/* Expanded items */}
                    {isExpanded && moreCount > 0 && (
                      <div style={{
                        padding: '0 var(--space-3) var(--space-3) var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderTop: '1px dashed var(--color-border)'
                      }}>
                        {items.slice(1).map((itemLine, idx) => {
                          const item = parseItemLine(itemLine)
                          return (
                            <div
                              key={idx}
                              style={{
                                padding: 'var(--space-2) 0',
                                borderBottom: idx < items.length - 2 ? '1px solid var(--color-border)' : 'none'
                              }}
                            >
                              <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                                {item?.name || itemLine}
                              </div>
                              {item?.details && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                                  {item.details}
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 'var(--space-2)', fontSize: '0.75rem', marginTop: 2 }}>
                                <span>×{item?.qty || 1}</span>
                                {item?.sp != null && <span style={{ color: 'var(--color-success)' }}>SP £{item.sp}</span>}
                                {item?.bp != null && <span style={{ color: 'var(--color-info)' }}>BP £{item.bp}</span>}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          onClose={closeDealModal}
          onDealUpdated={refreshDeals}
        />
      )}
    </div>
  )
}

export default AllDeals
